type StudioPerfDetails = Record<string, string | number | boolean | undefined>;

type StudioPerfEntry = {
  count: number;
  totalMs: number;
  maxMs: number;
  lastDetails?: StudioPerfDetails;
};

type StudioPerfRow = {
  name: string;
  count: number;
  totalMs: number;
  avgMs: number;
  maxMs: number;
  last?: string;
};

type StudioPerfDebugApi = {
  enabled?: boolean;
  flush: () => void;
  printTotals: () => void;
  reset: () => void;
};

declare global {
  interface Window {
    __studioPerfDebug?: StudioPerfDebugApi;
  }
}

const intervalEntries = new Map<string, StudioPerfEntry>();
const totalEntries = new Map<string, StudioPerfEntry>();
let flushTimer: number | undefined;
let lastFlushAt = 0;

const isTestRuntime = (): boolean =>
  typeof process !== "undefined" && process.env?.NODE_ENV === "test";

const isLocalHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
};

const getDebugApi = (): StudioPerfDebugApi | undefined => {
  if (typeof window === "undefined") return undefined;
  if (!window.__studioPerfDebug) {
    window.__studioPerfDebug = {
      flush: () => flushStudioPerfDebug(),
      printTotals: () => printStudioPerfRows("totals", totalEntries),
      reset: () => {
        intervalEntries.clear();
        totalEntries.clear();
        lastFlushAt = studioPerfNow();
      },
    };
  }
  return window.__studioPerfDebug;
};

const isEnabled = (): boolean => {
  if (isTestRuntime() || !isLocalHost()) return false;
  const api = getDebugApi();
  if (api?.enabled !== undefined) return api.enabled;
  return window.localStorage?.getItem("studioPerfDebug") === "on";
};

export const studioPerfNow = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const getEntry = (
  entries: Map<string, StudioPerfEntry>,
  name: string
): StudioPerfEntry => {
  const existingEntry = entries.get(name);
  if (existingEntry) return existingEntry;
  const entry = { count: 0, totalMs: 0, maxMs: 0 };
  entries.set(name, entry);
  return entry;
};

const addEntry = (
  entries: Map<string, StudioPerfEntry>,
  name: string,
  durationMs: number,
  details?: StudioPerfDetails
): void => {
  const entry = getEntry(entries, name);
  entry.count += 1;
  entry.totalMs += durationMs;
  entry.maxMs = Math.max(entry.maxMs, durationMs);
  entry.lastDetails = details;
};

const formatDetails = (details?: StudioPerfDetails): string | undefined => {
  if (!details) return undefined;
  try {
    const serialized = JSON.stringify(details);
    return serialized.length > 160
      ? `${serialized.slice(0, 157)}...`
      : serialized;
  } catch {
    return "[unserializable]";
  }
};

const buildRows = (entries: Map<string, StudioPerfEntry>): StudioPerfRow[] =>
  Array.from(entries.entries())
    .map(([name, entry]) => ({
      name,
      count: entry.count,
      totalMs: Number(entry.totalMs.toFixed(1)),
      avgMs: Number((entry.totalMs / entry.count).toFixed(3)),
      maxMs: Number(entry.maxMs.toFixed(1)),
      last: formatDetails(entry.lastDetails),
    }))
    .sort((first, second) => second.totalMs - first.totalMs);

const printStudioPerfRows = (
  label: string,
  entries: Map<string, StudioPerfEntry>
): void => {
  const rows = buildRows(entries);
  if (!rows.length) return;
  console.log(`[StudioPerfDebug] ${label}`);
  console.table(rows);
};

const flushStudioPerfDebug = (): void => {
  flushTimer = undefined;
  const now = studioPerfNow();
  const elapsedMs = lastFlushAt ? now - lastFlushAt : 0;
  printStudioPerfRows(
    `last ${Math.max(1, Math.round(elapsedMs))}ms`,
    intervalEntries
  );
  intervalEntries.clear();
  lastFlushAt = now;
};

const scheduleFlush = (): void => {
  if (typeof window === "undefined" || flushTimer !== undefined) return;
  flushTimer = window.setTimeout(flushStudioPerfDebug, 1000);
};

export const recordStudioPerf = (
  name: string,
  durationMs = 0,
  details?: StudioPerfDetails
): void => {
  if (!isEnabled()) return;
  if (!lastFlushAt) lastFlushAt = studioPerfNow();
  addEntry(intervalEntries, name, durationMs, details);
  addEntry(totalEntries, name, durationMs, details);
  scheduleFlush();
};
